<template>
    <Dialog :visible="visible" modal dismissableMask :closable="false" :draggable="false"
        :style="{ width: '50rem', maxWidth: '95vw' }" contentClass="!p-0 overflow-hidden rounded-lg"
        :pt="{ header: { class: '!p-0' } }" @update:visible="v => !v && handleClose()">
        <!-- HEADER -->
        <template #header>
            <div
                class="flex items-center gap-4 w-full p-3 rounded-t-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <i class="fa-solid fa-users text-white text-xl" />
                </div>

                <div class="flex-1 min-w-0">
                    <h4 class="text-xl font-bold">
                        {{ title }}
                    </h4>
                    <p class="">
                        {{ users.length }} {{ users.length === 1 ? t('userGroups.user') : t('userGroups.users') }}
                    </p>
                </div>

                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    @click="handleClose()" />
            </div>
        </template>

        <!-- BODY -->
        <div class="flex flex-col" style="min-height: 400px; max-height: 600px;">
            <!-- Loading State -->
            <div v-if="loading" class="flex flex-col justify-center items-center flex-1 py-12">
                <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
                <p class="mt-4">{{ t('common.loading') }}</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="users.length === 0" class="flex flex-col items-center justify-center flex-1 py-12 px-6">
                <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-surface-100 mb-4">
                    <i class="fa-regular fa-users text-4xl" />
                </div>
                <h4 class="font-semibold text-surface-900 text-lg mb-2">{{ t('userGroups.noUsersInGroup') }}</h4>
                <p class="text-center mb-6">{{ t('userGroups.addUsersToGetStarted') }}</p>
            </div>

            <!-- Users List -->
            <div v-else class="flex-1 overflow-y-auto p-3">
                <!-- User Cards -->
                <div class="space-y-3">
                    <div v-for="user in users" :key="user.id"
                        class="flex items-center gap-3 p-2 rounded-xl bg-white border-2">
                        <!-- User Info -->
                        <div class="flex-1">
                            <h5 class="font-bold truncate">
                                {{ user.firstName }} {{ user.lastName }}
                            </h5>
                            <p class="truncate flex items-center gap-1">
                                <i class="fa-regular fa-envelope" />
                                <span>{{ user.email }}</span>
                            </p>
                        </div>

                        <!-- Role Badge -->
                        <div class="flex-shrink-0">
                            <Tag :value="user.role.name" severity="info" class="font-medium" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- FOOTER -->
        <template #footer>
            <div class="flex justify-end gap-3 p-3 border-t bg-gray-50 w-full">
                <Button :label="t('common.close')" icon="fa-solid fa-xmark" severity="secondary" outlined
                    @click="handleClose()" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { User } from '@/lib/types/user';
import { useI18n } from 'vue-i18n';

interface Props {
    visible: boolean;
    title: string;
    users: User[];
    loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    loading: false
});

const emit = defineEmits(['close']);

const { t } = useI18n();

function handleClose() {
    emit('close');
}
</script>
